import {
  cloneAddress,
  isAddressComplete,
  normalizeCustomerAddress,
} from "./addressModel";

export const MASTER_REFRESH_FIELDS = [
  "companyName",
  "organizationNumber",
  "vatNumber",
  "contactPerson",
  "email",
  "telephone",
  "paymentMethod",
  "paymentTermsDays",
  "distributionMethod",
  "invoiceEmail",
  "currencyCode",
];

export const createCustomerCardId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `customer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getLocalDateValue = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const comparableValue = value =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("sv");

const sameValue = (left, right) => comparableValue(left) === comparableValue(right);

const shouldRefreshFromMaster = (currentValue, previousMasterValue) =>
  previousMasterValue === undefined ||
  sameValue(currentValue, previousMasterValue);

const existingOr = (existingCard, field, fallback) =>
  existingCard ? existingCard[field] ?? fallback : fallback;

export const createInitialDraft = ({ organization, existingCard, baseline }) => {
  const hasExistingCard = Boolean(existingCard);
  const organizationId = hasExistingCard
    ? existingCard?.organizationId ?? null
    : organization?.id ?? null;
  const isInitiallyLinked = Boolean(
    organizationId && organization?.id === organizationId
  );
  const initialBaseline = isInitiallyLinked ? baseline : null;
  const companyName = existingOr(
    existingCard,
    "companyName",
    initialBaseline?.companyName ?? ""
  );

  return {
    customerCardId:
      existingCard?.customerCardId ?? createCustomerCardId(),
    organizationId,
    organizationPublicId: hasExistingCard
      ? existingCard?.organizationPublicId ?? null
      : organization?.publicCompanyId ?? null,
    memberId: hasExistingCard
      ? existingCard?.memberId ?? null
      : organization?.memberId ?? null,

    companyName,
    customerNumber: existingOr(
      existingCard,
      "customerNumber",
      organization?.customerNumber || (organizationId ? `K-${organizationId}` : "")
    ),
    organizationNumber: existingOr(
      existingCard,
      "organizationNumber",
      initialBaseline?.organizationNumber ?? ""
    ),
    vatNumber: existingOr(
      existingCard,
      "vatNumber",
      initialBaseline?.vatNumber ?? ""
    ),
    contactPerson: existingOr(
      existingCard,
      "contactPerson",
      initialBaseline?.contactPerson ?? ""
    ),
    email: existingOr(existingCard, "email", initialBaseline?.email ?? ""),
    telephone: existingOr(
      existingCard,
      "telephone",
      initialBaseline?.telephone ?? ""
    ),

    customerOwner: existingCard?.customerOwner ?? "",
    customerStatus: existingCard?.customerStatus ?? "ACTIVE",
    activeFrom: existingCard?.activeFrom ?? getLocalDateValue(),
    relationshipNote: existingCard?.relationshipNote ?? "",

    logoDataUrl: existingCard?.logoDataUrl ?? "",
    logoHidden: Boolean(existingCard?.logoHidden),

    billingAddress: normalizeCustomerAddress(
      existingCard?.billingAddress ?? initialBaseline?.billingAddress
    ),
    deliverySameAsBilling:
      existingCard?.deliverySameAsBilling ??
      initialBaseline?.deliverySameAsBilling ??
      true,
    deliveryAddress: normalizeCustomerAddress(
      existingCard?.deliveryAddress ?? initialBaseline?.deliveryAddress
    ),

    paymentMethod: existingOr(
      existingCard,
      "paymentMethod",
      initialBaseline?.paymentMethod || "INVOICE"
    ),
    paymentTermsDays: existingCard
      ? existingCard?.paymentTermsDays ?? 30
      : initialBaseline?.paymentTermsDays ?? 30,
    distributionMethod: existingOr(
      existingCard,
      "distributionMethod",
      initialBaseline?.distributionMethod || "EMAIL"
    ),
    invoiceEmail: existingOr(
      existingCard,
      "invoiceEmail",
      initialBaseline?.invoiceEmail ?? ""
    ),
    currencyCode: existingOr(
      existingCard,
      "currencyCode",
      initialBaseline?.currencyCode || "SEK"
    ),

    purchaseOrderNumber: existingCard?.purchaseOrderNumber ?? "",
    yourReference: existingCard?.yourReference ?? "",
    ourReference: existingCard?.ourReference ?? "",
    projectNumber: existingCard?.projectNumber ?? "",
    invoiceText: existingCard?.invoiceText ?? "",
    notes: existingCard?.notes ?? "",
  };
};

/*
 * Follow a changed organization master value only while the customer value
 * still equals the previous master value. Empty new master values are valid
 * and must propagate; defaults belong only in createInitialDraft().
 */
export const refreshDraftFromScalarBaseline = (
  current,
  previousBaseline,
  nextBaseline
) => {
  if (!nextBaseline) return current;

  const result = { ...current };
  MASTER_REFRESH_FIELDS.forEach(field => {
    if (
      !previousBaseline ||
      shouldRefreshFromMaster(current[field], previousBaseline[field])
    ) {
      result[field] = nextBaseline[field] ?? "";
    }
  });

  return result;
};

export const linkOrganizationDraft = (current, organization, baseline) => {
  const selectedId = organization?.id ?? null;
  const sameOrganization = selectedId === current.organizationId;

  if (sameOrganization) {
    return {
      ...current,
      organizationId: selectedId,
      organizationPublicId: organization?.publicCompanyId ?? null,
      memberId: organization?.memberId ?? null,
      logoHidden: false,
    };
  }

  return {
    ...current,
    organizationId: selectedId,
    organizationPublicId: organization?.publicCompanyId ?? null,
    memberId: organization?.memberId ?? null,

    companyName: baseline?.companyName ?? "",
    customerNumber:
      current.customerNumber ||
      organization?.customerNumber ||
      (selectedId ? `K-${selectedId}` : ""),
    organizationNumber: baseline?.organizationNumber ?? "",
    vatNumber: baseline?.vatNumber ?? "",
    contactPerson: baseline?.contactPerson ?? "",
    email: baseline?.email ?? "",
    telephone: baseline?.telephone ?? "",

    logoDataUrl: "",
    logoHidden: false,

    billingAddress: cloneAddress(baseline?.billingAddress),
    deliveryAddress: cloneAddress(baseline?.deliveryAddress),
    deliverySameAsBilling: baseline?.deliverySameAsBilling ?? true,

    paymentMethod: baseline?.paymentMethod || current.paymentMethod,
    paymentTermsDays:
      baseline?.paymentTermsDays !== "" && baseline?.paymentTermsDays != null
        ? baseline.paymentTermsDays
        : current.paymentTermsDays,
    distributionMethod:
      baseline?.distributionMethod || current.distributionMethod,
    invoiceEmail: baseline?.invoiceEmail || current.invoiceEmail,
    currencyCode: baseline?.currencyCode || current.currencyCode,
  };
};

export const unlinkOrganizationDraft = current => ({
  ...current,
  organizationId: null,
  organizationPublicId: null,
  memberId: null,
  distributionMethod:
    current.distributionMethod === "UNIQUEUE"
      ? "EMAIL"
      : current.distributionMethod,
});

export const applyAddressBaselineToDraft = (current, addressBaseline) => {
  if (!addressBaseline) return current;

  return {
    ...current,
    billingAddress: cloneAddress(addressBaseline.billingAddress),
    deliveryAddress: cloneAddress(addressBaseline.deliveryAddress),
    deliverySameAsBilling: addressBaseline.deliverySameAsBilling,
  };
};

export const isCustomerDraftSaveable = draft => {
  const deliveryValid =
    draft.deliverySameAsBilling || isAddressComplete(draft.deliveryAddress);

  return Boolean(
    draft.companyName.trim() &&
      draft.customerNumber.trim() &&
      isAddressComplete(draft.billingAddress) &&
      deliveryValid &&
      draft.paymentMethod &&
      Number(draft.paymentTermsDays) >= 0
  );
};

export const buildCustomerPayload = (draft, existingCard) => ({
  ...draft,
  linkedOrganization: Boolean(draft.organizationId),
  previousOrganizationId: existingCard?.organizationId ?? null,
  paymentTermsDays: Number(draft.paymentTermsDays) || 0,
  billingAddress: cloneAddress(draft.billingAddress),
  deliveryAddress: cloneAddress(
    draft.deliverySameAsBilling ? draft.billingAddress : draft.deliveryAddress
  ),
});
